import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, Output } from "@angular/core";
import {
  AllCommunityModule,
  CellEditingStoppedEvent,
  type ColDef,
  DataTypeDefinition,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  ModuleRegistry,
  type RowSelectionOptions,
  themeQuartz
} from "ag-grid-community";
import { ColumnDto, TableDataType, TableMenuOption, TableMeta } from "../../models/table-meta.interface";
import { CustomLoadingOverlayComponent } from "./custom-loading-overlay";
import { CustomNoDataFound } from "./custom-no-data-found";
import { AgGridTableCellComponent } from "./ag-grid-table-cell/ag-grid-table-cell.component";
import { AgGridAngular } from "ag-grid-angular";
import { GridTableService } from "../../services/grid-table.service";
import { LoadingService } from "../../services/loading.service";
import { MultiSelectComponent } from "../select/multi-select.component";
import { DynamicFromDataType } from "../../constants/dynamic-from-data-type";
import { AutocompleteDropdownComponent } from "../autocomplete-dropdown/autocomplete-dropdown.component";
import { DatePickerComponent } from "../common/date-picker/date-picker.component";
import { DynamicFormService } from "../../services/dynamic-form.service";
import { FormBuilder } from "@angular/forms";
import { InputComponent } from "../input/input.component";
import { AgGridFilterComponent } from "./ag-grid-filter/ag-grid-filter.component";

export const AgGridDataTypes = {
  action: "action",
  menuOptions: "menuOptions",
  booleanToggle: "booleanToggle",
  all: () => {
    return [AgGridDataTypes.action, AgGridDataTypes.menuOptions, AgGridDataTypes.booleanToggle];
  }
};

// Lazy load AG Grid modules only when needed
ModuleRegistry.registerModules([AllCommunityModule]);

const DEFAULT_ACTION_COL: ColDef = {
  field: AgGridDataTypes.action,
  cellDataType: AgGridDataTypes.action,
  sortable: false,
  pinned: "right",
  lockPinned: true,
  resizable: false,
  lockVisible: true,
  filter: false,
  headerName: "",
  width: 56,
  minWidth: 56,
  maxWidth: 56,
  cellRenderer: AgGridTableCellComponent,
  cellRendererParams: {}
};

@Component({
  selector: "app-ag-grid-wrapper",
  standalone: true,
  imports: [
    AgGridAngular
  ],
  templateUrl: "./ag-grid-wrapper.component.html",
  styleUrl: "./ag-grid-wrapper.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush // Add OnPush for better performance
})
export class AgGridWrapperComponent implements OnDestroy {
  public gridApi!: GridApi;

  private resizeObserver?: ResizeObserver;
  private resizeTimeout?: number;
  private gridContainer?: HTMLElement;
  private textMeasureContext: CanvasRenderingContext2D | null = null;
  private previousWidth: number = 0;
  private suppressSelectionChangeEvent: boolean = false;

  isLoading: boolean = false;
  defaultMaxWidth = 500;
  defaultMinWidth = 80;
  tableColumns: ColDef[] = [];
  defaultColDef: ColDef = {
    cellStyle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      fontWeight: 300
    },
    headerStyle: {},
    // suppressKeyboardEvent: params => {
    //   const key = (params.event as KeyboardEvent).key;
    //   return params.editing && key === "Enter";
    // }
  };
  dataTypeDefinitions: { [p: string]: DataTypeDefinition<any> } | undefined = {
    [AgGridDataTypes.action]: {
      extendsDataType: "text",
      baseDataType: "text"
    },
    [AgGridDataTypes.menuOptions]: {
      extendsDataType: "text",
      baseDataType: "text"
    },
    [TableDataType.STRING]: {
      extendsDataType: "text",
      baseDataType: "text"
    },
    [TableDataType.BOOLEAN]: {
      extendsDataType: "boolean",
      baseDataType: "boolean"
    },
    [TableDataType.ARRAY]: {
      extendsDataType: "object",
      baseDataType: "object"
    },
    [TableDataType.OBJECT]: {
      extendsDataType: "object",
      baseDataType: "object"
    },
    [TableDataType.DATETIME]: {
      extendsDataType: "text",
      baseDataType: "text"
    },
    [TableDataType.DATE]: {
      extendsDataType: "text",
      baseDataType: "text"
    },
    [TableDataType.NUMBER]: {
      extendsDataType: "text",
      baseDataType: "text"
    }
  };
  rowSelection: RowSelectionOptions = this.getRowSelectionConfig();
  selectionColumnDef: ColDef = this.getSelectionColumnDef();
  initialState = {};
  tosTheme = themeQuartz.withParams({
    accentColor: "#1062B9",
    borderColor: "#F2F4F7",
    browserColorScheme: "light",
    cellTextColor: "#101828",
    columnBorder: false,
    dataFontSize: 13,
    fontFamily: {
      googleFont: "Inter"
    },
    foregroundColor: "#101828",
    headerBackgroundColor: "#F5F5F4",
    headerColumnResizeHandleHeight: "25%",
    headerColumnResizeHandleWidth: "20px",
    headerFontSize: 12,
    headerFontWeight: 400,
    headerHeight: 44,
    rowHeight: 55,
    headerRowBorder: true,
    rowBorder: true,
    rowHoverColor: "#F9FAFB",
    rowVerticalPaddingScale: 1.5,
    spacing: 8,
    wrapperBorder: true,
    wrapperBorderRadius: 0
  });
  loadingOverlayComponent = CustomLoadingOverlayComponent;
  readonly CustomNoDataFound = CustomNoDataFound;

  @Input() tableRows: any[] = [];
  @Input() tableMeta!: TableMeta;
  @Input() tableMenuOptions: TableMenuOption[] = [];
  @Input() showMenuOptions: boolean = true;
  @Input() orderBy: { [key: string]: "asc" | "desc" } = {};
  @Input() domLayout: "autoHeight" | "normal" | "print" = "normal";
  @Input() rowSelectionMode: "single" | "multiple" | "none" = "multiple";
  @Input() readOnly: boolean = false;
  @Input() deletionAllowed: boolean = false;
  @Input() selectedItems: any[] = [];
  @Input() allTableRecordsSelected: boolean = false;
  @Input() skipDotNotationLookup: boolean = false;

  @Output() selectedItemsChange = new EventEmitter<any[]>();
  @Output() rowActionTriggered = new EventEmitter<any>();
  @Output() rowDeleted = new EventEmitter<any>();
  @Output() tableMenuOptionSelected = new EventEmitter<any>();
  @Output() sortChange = new EventEmitter<any>();
  @Output() cellValueChanged = new EventEmitter<any>();
  @Output() cellEditingStopped = new EventEmitter<CellEditingStoppedEvent>();


  constructor(private gridTableService: GridTableService,
              private loadingService: LoadingService,
              private cdr: ChangeDetectorRef,
              private hostElementRef: ElementRef<HTMLElement>,
              private dynamicFormService: DynamicFormService,
              private fb: FormBuilder) {
  }

  ngOnChanges(changes: any): void {
    if (changes["rowSelectionMode"]) {
      this.rowSelection = this.getRowSelectionConfig();
      this.selectionColumnDef = this.getSelectionColumnDef();
    }

    if (!this.gridApi) {
      return;
    }

    if (changes["tableRows"]) {
      // Suppress transient deselection events fired during row data replacement.
      this.suppressSelectionChangeEvent = true;
    }

    if (changes["selectedItems"] || changes["allTableRecordsSelected"]) {
      this.syncSelectionFromInputs();
    }
  }

  ngOnInit() {
    this.updateTableColumns();

    this.loadingService.loading$
      .subscribe({
        next: data => {
          this.isLoading = data.isLoading && data.type === "inline";
          this.cdr.detectChanges();
        }
      });

    this.gridTableService.getGridState$().subscribe((savedState) => {
      if (savedState) {
        this.initialState = savedState;
      }
    });
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridTableService.gridInit(this.gridApi, (colId) => this.tableMeta.columns.find(c => c.fieldName === colId));
    this.optimiseColumnWidth(); //once grid is ready optimise the width
    this.setupResizeObserver();
    this.syncSelectionFromInputs();
  }

  private setupResizeObserver() {
    const gridElement = this.hostElementRef.nativeElement.querySelector(".ag-root-wrapper") as HTMLElement | null;

    if (!gridElement) {
      setTimeout(() => this.setupResizeObserver(), 100);
      return;
    }

    this.gridContainer = gridElement;

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;

        if (width > 0 && width !== this.previousWidth) {
          this.previousWidth = width;

          if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
          }

          this.resizeTimeout = window.setTimeout(() => {
            if (this.gridApi) {
              this.optimiseColumnWidth();
            }
          }, 150);
        }
      }
    });

    this.resizeObserver.observe(gridElement);
  }

  ngOnDestroy() {
    // Clean up ResizeObserver
    if (this.resizeObserver && this.gridContainer) {
      this.resizeObserver.unobserve(this.gridContainer);
      this.resizeObserver.disconnect();
    }

    // Clear any pending timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  private optimiseColumnWidth() {
    if (!this.gridApi) {
      return;
    }

    this.gridApi.autoSizeAllColumns();
    setTimeout(() => this.expandColumnsEquallyIfSpaceRemains(), 0);
  }

  private expandColumnsEquallyIfSpaceRemains() {
    if (!this.gridApi) {
      return;
    }

    try {
      const centerColumns = this.getCenterDisplayedColumns();
      if (centerColumns.length === 0) {
        return;
      }

      const currentCenterColumnsWidth = centerColumns.reduce((total: number, col: any) => {
        return total + (col.getActualWidth() || 0);
      }, 0);

      const centerViewportWidth = this.getCenterViewportWidth();
      if (centerViewportWidth <= 0) {
        return;
      }

      const remainingSpace = Math.floor(centerViewportWidth - currentCenterColumnsWidth);
      if (remainingSpace <= 1) {
        return;
      }

      const expandableColumns = centerColumns.filter((col: any) => this.isColumnExpandable(col));
      if (expandableColumns.length === 0) {
        return;
      }

      const widthIncrementPerColumn = Math.floor(remainingSpace / expandableColumns.length);
      const remainder = remainingSpace % expandableColumns.length;

      const columnWidthUpdates = expandableColumns.map((col: any, index: number) => {
        const remainderWidth = index < remainder ? 1 : 0;
        return {
          key: col.getColId(),
          newWidth: (col.getActualWidth() || 0) + widthIncrementPerColumn + remainderWidth
        };
      });

      this.gridApi.setColumnWidths(columnWidthUpdates);
    } catch (error) {
      console.warn("Error while resizing columns:", error);
    }
  }

  private getCenterDisplayedColumns(): any[] {
    return this.gridApi
      .getAllDisplayedColumns()
      .filter((col: any) => !col.getPinned());
  }

  private getCenterViewportWidth(): number {
    const centerViewport = this.gridContainer?.querySelector(".ag-center-cols-viewport") as HTMLElement | null;
    if (centerViewport?.clientWidth) {
      return centerViewport.clientWidth;
    }

    const bodyViewport = this.gridContainer?.querySelector(".ag-body-viewport") as HTMLElement | null;
    return bodyViewport?.clientWidth || 0;
  }

  private isColumnExpandable(column: any): boolean {
    const colDef = column?.getColDef?.();
    if (colDef?.resizable === false) {
      return false;
    }

    const colId = column?.getColId?.();
    return colId !== AgGridDataTypes.menuOptions && colId !== AgGridDataTypes.action;
  }

  saveGridState() {
    const state = this.gridApi.getState();
    this.gridTableService.saveGridState(state);
  }

  onSelectionChanged(): void {
    if (this.suppressSelectionChangeEvent) {
      return;
    }

    this.selectedItemsChange.emit(this.gridApi.getSelectedRows());
  };

  handleSort(event: any) {
    if (!this.gridApi)
      return;

    console.log(event);
    if (this.gridApi.getColumnDef(event.columns[0]?.getId())?.sortable) {
      this.sortChange.emit(event.columns[0]?.getId());
    }
  }

  updateTableColumns() {
    const dataColumns = this.buildDataColumns() ?? [];
    const tableActions = this.buildTableActions() ?? [];
    this.tableColumns = [
      ...dataColumns,
      ...tableActions
    ];

    const menuOptions = this.buildMenuOptions();
    if (this.showMenuOptions && this.tableMenuOptions.length > 0) {
      this.tableColumns.push(menuOptions);
    }
  }

  private getDeleteAction(): ColDef {
    return {
      ...DEFAULT_ACTION_COL,
      cellRendererParams: {
        columnDto: {
          fieldName: "",
          displayName: "",
          dataType: "STRING",
          columnType: "ACTION",
          actionConfig: {
            "label": "",
            "actionName": "delete",
            "writeOperation": false
          },
          styleConfig: {
            type: "button",
            icon: "assets/icons/delete_icon.svg",
            "styleProperties": {
              "display": "flex",
              "gap": "4px",
              "alignItems": "center",
              "background": "transparent",
              "color": "#1062B9",
              "fontSize": "14px",
              "fontStyle": "normal",
              "border": "none",
              "fontWeight": 500,
              "lineHeight": "20px",
              "padding": 0
            },
            iconStyleProperties: {
              background: "var(--error-600)",
              height: "16px",
              width: "16px",
              display: "flex"
            }
          }
        },
        actionClicked: {
          emit: (eventActionAndRecord: any) => {
            // this delete action specific handler
            this.rowDeleted.emit(eventActionAndRecord);
          }
        }
      }
    };
  }

  private buildTableActions(): ColDef[] {
    if (this.readOnly) {
      return [];
    }

    const actions = (this.tableMeta?.actions ?? []);
    const tableActions: ColDef[] = actions.map(action => {
      const configuredWidth = this.getWidthFromStyleConfig(action.thStyleConfig);
      const actionColumnWidth = this.getActionColumnWidth(action, configuredWidth);
      return {
        ...DEFAULT_ACTION_COL,
        width: actionColumnWidth,
        minWidth: actionColumnWidth,
        maxWidth: actionColumnWidth,
        cellRendererParams: {
          columnDto: action,
          actionClicked: this.rowActionTriggered,
        }
      };
    });
    this.deletionAllowed && tableActions.push(this.getDeleteAction());
    return tableActions;
  }

  private getWidthFromStyleConfig(styleConfig: any): number | undefined {
    const width = styleConfig?.width;
    if (typeof width === "number") {
      return width;
    }
    if (typeof width !== "string") {
      return undefined;
    }
    const parsedWidth = Number.parseInt(width.replace("px", ""), 10);
    return Number.isFinite(parsedWidth) ? parsedWidth : undefined;
  }

  private getActionColumnWidth(action: ColumnDto, configuredWidth?: number): number {
    const minIconActionWidth = 56;
    const configuredMinWidth = configuredWidth ?? 0;
    const label = action?.actionConfig?.label?.trim() || "";
    if (!label) {
      return Math.max(configuredMinWidth, minIconActionWidth);
    }

    const styleProperties = action?.styleConfig?.styleProperties || {};
    const fontSize = this.getPixelValue(styleProperties["fontSize"], 14);
    const fontWeight = styleProperties["fontWeight"] || 500;
    const hasIcon = !!action?.styleConfig?.icon;
    const iconWidth = hasIcon ? this.getPixelValue(action?.styleConfig?.iconStyleProperties?.["width"], 16) : 0;
    const gap = hasIcon ? this.getPixelValue(styleProperties["gap"], 4) : 0;

    const textWidth = this.measureTextWidth(label, fontSize, fontWeight);
    const cellPaddingAndBuffer = 40;
    const estimatedWidth = Math.ceil(textWidth + iconWidth + gap + cellPaddingAndBuffer);

    return Math.max(configuredMinWidth, minIconActionWidth, estimatedWidth);
  }

  private measureTextWidth(text: string, fontSize: number, fontWeight: string | number): number {
    const fallbackTextWidth = Math.ceil(text.length * fontSize * 0.62);

    if (typeof document === "undefined") {
      return fallbackTextWidth;
    }

    if (!this.textMeasureContext) {
      const canvas = document.createElement("canvas");
      this.textMeasureContext = canvas.getContext("2d");
    }

    if (!this.textMeasureContext) {
      return fallbackTextWidth;
    }

    this.textMeasureContext.font = `${fontWeight} ${fontSize}px Inter`;
    return Math.ceil(this.textMeasureContext.measureText(text).width);
  }

  private getPixelValue(value: string | number | undefined, defaultValue: number): number {
    if (typeof value === "number") {
      return value;
    }

    if (typeof value === "string") {
      const parsedValue = Number.parseFloat(value.replace("px", "").trim());
      if (Number.isFinite(parsedValue)) {
        return parsedValue;
      }
    }

    return defaultValue;
  }

  private buildDataColumns(): ColDef[] {
    const columns = this.tableMeta?.columns?.map((col) => {
      return {
        field: col.fieldName,
        sortable: !!col.sortable,
        sort: this.orderBy?.[col.fieldName] ?? null,
        sortIndex: null,
        resizable: true,
        filter: !!col.filterable,
        floatingFilter: !!col.filterable && this.tableRows.length > 0,
        floatingFilterComponent: AgGridFilterComponent,
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
        type: col.dataType,
        headerName: col.displayName,
        cellDataType: col.dataType,
        editable: !this.readOnly && col.editable && col.dataType != "booleanToggle", // boolean datatypes don't need to support double click editing
        cellRenderer: AgGridTableCellComponent,
        headerClass: "grid-header",
        refData: col.valueMap,
        cellEditor: this.getCellEditor(col),
        cellEditorParams: (agParams: any) => {
          // Resolve any validator params that are string field references into the row's actual value.
          // e.g. { type: "max", params: "baseAmount" } → { type: "max", params: row.baseAmount }
          const hasStringParams = col.editableConfig?.validators?.some(v => typeof v.params === "string");
          const resolvedCol = hasStringParams
            ? {
                ...col,
                editableConfig: {
                  ...col.editableConfig,
                  validators: col.editableConfig!.validators!.map(v =>
                    typeof v.params === "string"
                      ? { ...v, params: agParams.data?.[v.params] }
                      : v
                  ),
                },
              }
            : col;
          return {
            col: resolvedCol,
            ...col.editableConfig,
            ...col.editableConfig?.lookupDetails,
            showIcon: false,
          };
        },
        cellEditorPopup: false,
        cellRendererParams: {
          columnDto: col,
          actionClicked: this.rowActionTriggered,
          readOnly: this.readOnly,
          skipDotNotationLookup: this.skipDotNotationLookup
        }
      };
    });
    return columns || [];
  }

  private buildMenuOptions(): ColDef {
    return {
      field: AgGridDataTypes.menuOptions,
      sortable: false,
      hide: !this.showMenuOptions,
      filter: false,
      lockPinned: true,
      lockVisible: true,
      resizable: false,
      lockPosition: "right",
      headerName: "",
      pinned: "right",
      cellClass: "grid-row-menu",
      cellDataType: AgGridDataTypes.menuOptions,
      maxWidth: 60,
      cellRenderer: AgGridTableCellComponent,
      cellRendererParams: {
        menuOptions: this.tableMenuOptions,
        menuOptionSelected: this.tableMenuOptionSelected
      }
    };
  }


  private getCellEditor(col: ColumnDto): any {
    if (col.editableConfig) {
      const { inputType } = col.editableConfig;
      switch (inputType) {
        case DynamicFromDataType.PICKLIST:
          return AutocompleteDropdownComponent;
        case DynamicFromDataType.SELECT:
        case DynamicFromDataType.MULTI_SELECT:
          return MultiSelectComponent;
        case DynamicFromDataType.DATE:
        case DynamicFromDataType.DATETIME_LOCAL:
          return DatePickerComponent;
        case TableDataType.BOOLEAN:
          return MultiSelectComponent;
        default:
          return InputComponent;
      }
    }

    // Fall back to AG Grid's built-in editors based on dataType
    // switch (col.dataType) {
    //   case TableDataType.BOOLEAN:
    //     return "agCheckboxCellEditor";
    //   case TableDataType.DATETIME:
    //   case TableDataType.DATE:
    //     return "agDateCellEditor";
    //   case TableDataType.NUMBER:
    //     return "agNumberCellEditor";
    //   default:
    //     return "agTextCellEditor";
    // }
  }

  onFirstDataRendered(event: FirstDataRenderedEvent<any>) {
    this.optimiseColumnWidth();
    this.syncSelectionFromInputs();
  }

  onRowDataUpdated(): void {
    this.optimiseColumnWidth();
    this.syncSelectionFromInputs();
  }

  private syncSelectionFromInputs(): void {
    if (!this.gridApi) {
      return;
    }

    this.suppressSelectionChangeEvent = true;
    try {
      if (this.allTableRecordsSelected) {
        this.gridApi.selectAll();
        return;
      }

      if (!this.selectedItems || this.selectedItems.length === 0) {
        this.gridApi.deselectAll();
        return;
      }

      const selectedList = this.selectedItems;
      this.gridApi.forEachNode((node) => {
        const record = node.data;
        const shouldBeSelected = selectedList.some((selectedItem) => this.isSameRecord(selectedItem, record));

        if (node.isSelected() !== shouldBeSelected) {
          node.setSelected(shouldBeSelected);
        }
      });
    } finally {
      setTimeout(() => {
        this.suppressSelectionChangeEvent = false;
      });
    }
  }

  private isSameRecord(left: any, right: any): boolean {
    const uniqueColumns = this.tableMeta?.uniqueColumns ?? [];
    if (uniqueColumns.length > 0) {
      return uniqueColumns.every((key: string) => this.getValueByPath(left, key) === this.getValueByPath(right, key));
    }

    if (left?.id !== undefined && left?.id !== null && right?.id !== undefined && right?.id !== null) {
      return String(left.id) === String(right.id);
    }

    if (left?.randomId !== undefined && left?.randomId !== null && right?.randomId !== undefined && right?.randomId !== null) {
      return String(left.randomId) === String(right.randomId);
    }

    return JSON.stringify(left) === JSON.stringify(right);
  }

  private getValueByPath(source: any, path: string): any {
    if (!path?.includes(".")) {
      return source?.[path];
    }

    return path.split(".").reduce((acc, part) => acc?.[part], source);
  }

  private getRowSelectionConfig(): RowSelectionOptions {
    if (this.rowSelectionMode === "none") {
      return {
        mode: "multiRow",
        checkboxes: false,
        enableClickSelection: false,
        headerCheckbox: false,
      };
    }

    if (this.rowSelectionMode === "single") {
      return {
        mode: "singleRow",
        enableClickSelection: false
      };
    }

    return {
      mode: "multiRow",
      enableClickSelection: false
    };
  }

  private getSelectionColumnDef(): ColDef {
    return { hide: this.rowSelectionMode === "none" };
  }

  onCellValueChanged(event: any) {
    this.cellValueChanged.emit(event);
  }

  onCellEditingStopped(event: CellEditingStoppedEvent<any>) {
    // if (event.column) {
    //   this.gridApi.autoSizeColumns([event.column.getColId()]);
    // }
    this.cellEditingStopped.emit(event);
  }

  private transformTableData() {
    if (!this.tableRows || !this.tableMeta?.columns) return;

    this.tableRows = this.tableRows.map((item: any) => {
      const transformedItem = { ...item };

      this.tableMeta.columns
        .filter(header => header?.editableConfig?.lookupDetails)
        .forEach((header) => {
          const responseBindLabel = header.editableConfig?.lookupDetails?.responseBindLabel;
          const responseBindValue = header.editableConfig?.lookupDetails?.responseBindValue;
          const bindLabel = header.editableConfig?.lookupDetails?.bindLabel;
          const bindValue = header.editableConfig?.lookupDetails?.bindValue;
          const fieldKey = header?.fieldName;

          if (responseBindLabel && responseBindValue && bindLabel && bindValue && fieldKey) {
            // Get the values from the API response using responseBindLabel and responseBindValue
            const apiValue = item[responseBindValue];
            const apiLabel = item[responseBindLabel];

            if (apiValue !== undefined && apiLabel !== undefined) {
              transformedItem[fieldKey] = {
                key: apiValue,                    // For autocomplete component
                value: apiLabel,                  // For autocomplete component
                [bindLabel]: apiLabel,            // For cell renderer display
                [bindValue]: apiValue             // For cell renderer (if needed)
              };
            }
          }
        });

      return transformedItem;
    });

    console.log("transformedItems: ", this.tableRows);
  }

  /**
   * Transforms table data back to API format by extracting the actual values
   * from the autocomplete objects
   */
  public transformDataForApi(data: any[]): any[] {
    if (!data || !this.tableMeta?.columns) return data;

    return data.map((item: any) => {
      const apiItem = { ...item };

      this.tableMeta.columns
        .filter(header => header?.editableConfig?.lookupDetails)
        .forEach((header) => {
          const bindValue = header.editableConfig?.lookupDetails?.bindValue;
          const fieldKey = header?.fieldName;

          if (bindValue && fieldKey && apiItem[fieldKey]) {
            const fieldData = apiItem[fieldKey];
            if (typeof fieldData === "object" && fieldData !== null) {
              if (fieldData.hasOwnProperty('key')) {
                apiItem[fieldKey] = fieldData.key;
              } else if (fieldData.hasOwnProperty('id')) {
                apiItem[fieldKey] = fieldData['id'];
              } else if (fieldData.hasOwnProperty(bindValue)) {
                apiItem[fieldKey] = fieldData[bindValue];
              } else {
                apiItem[fieldKey] = fieldData;
              }
            } else {
              apiItem[fieldKey] = fieldData;
            }
          }
        });
      return apiItem;
    });
  }
}
